// Cookie consent banner for Google Analytics (Consent Mode v2). The gtag
// bootstrap in index.html scopes analytics_storage to denied-by-default only
// in opt-in-consent regions (EEA + UK + Switzerland, applied by Google from
// the visitor's location); this banner records the visitor's choice in
// localStorage ("cc-consent") and updates consent mode through the analytics
// wrapper. Shown only while no choice is stored AND the browser timezone
// looks like one of those regions; elsewhere analytics runs banner-free with
// privacy-policy disclosure. The timezone heuristic can only over-trigger
// (shows the banner to someone outside those regions); the region-scoped
// gtag defaults stay the compliance backstop when it under-triggers.
import { applyAnalyticsConsentChoice, storedAnalyticsConsent } from "./analytics.js";

// EEA + UK + CH timezones outside the Europe/ prefix: Iceland, Portugal's
// and Spain's Atlantic islands, and Cyprus.
const EXTRA_CONSENT_TIMEZONES = new Set([
  "Atlantic/Reykjavik",
  "Atlantic/Canary",
  "Atlantic/Madeira",
  "Atlantic/Azores",
  "Asia/Nicosia",
  "Asia/Famagusta",
]);

export function consentBannerRegionRequired(timeZone?: string): boolean {
  let zone = String(timeZone ?? "").trim();
  if (!zone) {
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    } catch {
      // No timezone signal: fail safe and show the banner.
      return true;
    }
  }
  if (!zone) return true;
  return zone.startsWith("Europe/") || EXTRA_CONSENT_TIMEZONES.has(zone);
}

export function mountConsentBanner(doc: Document = globalThis.document) {
  if (!doc?.body) return;
  if (storedAnalyticsConsent() !== null) return;
  if (!consentBannerRegionRequired()) return;
  if (doc.querySelector(".consent-banner")) return;

  const banner = doc.createElement("aside");
  banner.className = "consent-banner";
  banner.setAttribute("role", "region");
  banner.setAttribute("aria-label", "Cookie consent");
  banner.innerHTML = `
    <p class="consent-banner-copy">
      AlaryIQ uses analytics cookies to understand how the app is used and improve it.
      See our <a href="https://alaryiq.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
    </p>
    <div class="consent-banner-actions">
      <button class="secondary-action consent-banner-decline" type="button">Decline</button>
      <button class="primary-action consent-banner-accept" type="button">Accept</button>
    </div>
  `;

  const choose = (granted: boolean) => {
    applyAnalyticsConsentChoice(granted);
    banner.remove();
  };
  banner.querySelector(".consent-banner-decline")?.addEventListener("click", () => choose(false));
  banner.querySelector(".consent-banner-accept")?.addEventListener("click", () => choose(true));

  doc.body.append(banner);
}
